import {
  Coding,
  CodeableConcept,
  Location,
  Practitioner,
  Reference,
  Schedule,
  Extension,
  Timing,
  Resource,
  PractitionerRole,
  Slot,
} from '@medplum/fhirtypes';
import {
  capitalize,
  formatHumanName,
  getExtensionValue,
  getReferenceString,
  isResource,
  MedplumClient,
  ResourceArray,
} from '@medplum/core';
import {
  TIMEZONE_EXTENSION,
  SCHEDULING_PARAMETERS_EXTENSION,
  SCHEDULING_GUIDELINE_PARAMETER,
} from '@bariendo/lib/extensions';
import { ScheduleCategory, BARIENDO_CT_SYSTEM } from '@bariendo/lib/constants';
import { VIRTUAL_ENCOUNTER_CLASS_CODING } from '@bariendo/lib/codings';
import {
  TimeRange,
  WeeklyAvailabilityFormData,
  DayOfWeek,
  DAYS_OF_WEEK,
  AvailabilityModality,
  ProviderAvailability,
  AvailabilityEntry,
  OneTimeAvailabilityFormData,
} from '../Availability/types';
import { AvailabilityTypeEnum, FindAvailabilityWindow } from '@bariendo/lib/scheduling';
import { calculateEndTimeFromDuration, convertTimingDurationToHours } from '@bariendo/lib/time';
import { getAvatarInitials } from '@/services/organization';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

// Get timezone from location extension
export function getLocationTimezone(location: Location | null): string | null {
  if (!location) return null;
  return (getExtensionValue(location, TIMEZONE_EXTENSION) as string) || null;
}

export function convertTimeToHHMMSS(time: string | null): string {
  if (!time) return '00:00:00';
  // TimeInput (native input[type="time"]) returns "HH:mm" format (24-hour)
  // Convert to "HH:mm:ss" format
  const timeStr = time.trim();
  const match = timeStr.match(/(\d{1,2}):(\d{2})/);
  if (!match) return '00:00:00';

  const hours = match[1].padStart(2, '0');
  const minutes = match[2];

  return `${hours}:${minutes}:00`;
}

export function checkTimeRangeOverlap(timeRanges: TimeRange[]): boolean {
  for (let i = 0; i < timeRanges.length; i++) {
    for (let j = i + 1; j < timeRanges.length; j++) {
      const range1 = timeRanges[i];
      const range2 = timeRanges[j];

      const start1 = range1.startTime;
      const end1 = range1.endTime;
      const start2 = range2.startTime;
      const end2 = range2.endTime;

      // Check if ranges overlap
      if (
        (start1 >= start2 && start1 < end2) ||
        (end1 > start2 && end1 <= end2) ||
        (start1 <= start2 && end1 >= end2)
      ) {
        return true;
      }
    }
  }
  return false;
}

/** Merge overlapping availability windows so stacked blocks don't appear darker (week/month view) */
export function mergeOverlappingWindows(windows: FindAvailabilityWindow[]): FindAvailabilityWindow[] {
  if (windows.length === 0) return [];
  const sorted = [...windows].sort((a, b) => a.startTime.localeCompare(b.startTime));
  const merged: FindAvailabilityWindow[] = [{ startTime: sorted[0].startTime, endTime: sorted[0].endTime }];
  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const last = merged[merged.length - 1];
    if (current.startTime <= last.endTime) {
      if (current.endTime > last.endTime) {
        merged[merged.length - 1] = { startTime: last.startTime, endTime: current.endTime };
      }
    } else {
      merged.push({ startTime: current.startTime, endTime: current.endTime });
    }
  }
  return merged;
}

/**
 * Convert time from "HH:mm:ss" format to "HH:mm" format for form inputs
 */
export function convertTimeFromHHMMSS(time: string): string {
  if (!time) return '00:00';
  const match = time.match(/(\d{1,2}):(\d{2})/);
  if (!match) return '00:00';
  return `${match[1].padStart(2, '0')}:${match[2]}`;
}

/**
 * Fetch the Schedule resource for appointment availability for a provider
 */
export async function fetchProviderAvailabilitySchedule(
  medplum: MedplumClient,
  provider: Reference<Practitioner>
): Promise<Schedule | undefined> {
  return await medplum.searchOne('Schedule', {
    'actor:PractitionerRole.practitioner': getReferenceString(provider),
    'service-category': `${BARIENDO_CT_SYSTEM}|${ScheduleCategory.APPOINTMENT_AVAILABILITY}`,
  });
}

/**
 * Parse Schedule extensions into WeeklyAvailabilityFormData
 */
export function parseScheduleToFormData(
  schedule: Schedule | null,
  defaultPractitioner: Reference<Practitioner> | null,
  defaultTimezone: string
): WeeklyAvailabilityFormData | null {
  if (!schedule || !schedule.extension || schedule.extension.length === 0) {
    return null;
  }

  // Filter extensions with SCHEDULING_PARAMETERS_EXTENSION URL
  const schedulingExtensions = schedule.extension.filter(
    (ext: Extension) => ext.url === SCHEDULING_PARAMETERS_EXTENSION
  );

  if (schedulingExtensions.length === 0) {
    return null;
  }

  // Extract common properties from first extension
  const firstExt = schedulingExtensions[0];
  const timezone = (getExtensionValue(firstExt, 'timezone') as string) || defaultTimezone;
  const encounterClass = (getExtensionValue(firstExt, 'encounterClass') as Coding) || VIRTUAL_ENCOUNTER_CLASS_CODING;
  const locationReference = getExtensionValue(firstExt, 'location') as Reference<Location> | undefined;
  const location = locationReference?.reference
    ? ({ reference: locationReference.reference } as Reference<Location>)
    : null;

  // Group extensions by day and time range
  // Key format: "dayOfWeek|startTime|endTime"
  const timeRangeMap = new Map<string, { startTime: string; endTime: string; appointmentTypes: Coding[] }>();

  for (const ext of schedulingExtensions) {
    const timing = (getExtensionValue(ext, 'availability') as Timing | undefined)?.repeat;

    if (
      !timing ||
      !timing.dayOfWeek ||
      timing.dayOfWeek.length === 0 ||
      !timing.timeOfDay ||
      timing.timeOfDay.length < 1 ||
      !timing.duration ||
      !timing.durationUnit
    ) {
      continue;
    }

    const dayOfWeek = timing.dayOfWeek[0] as DayOfWeek;
    const startTimeHHMMSS = timing.timeOfDay[0] || '';
    const startTime = convertTimeFromHHMMSS(startTimeHHMMSS);

    let durationHours: number;
    try {
      durationHours = convertTimingDurationToHours(timing.duration, timing.durationUnit);
    } catch {
      continue;
    }
    const endTimeHHMMSS = calculateEndTimeFromDuration(startTimeHHMMSS, durationHours);
    const endTime = convertTimeFromHHMMSS(endTimeHHMMSS);

    if (!startTime || !endTime) {
      continue;
    }

    // Get appointment types from serviceType. Read every coding, not just the
    // first: FHIR allows a CodeableConcept to carry several, and saving the
    // drawer rewrites the whole Schedule from form state — so a type the form
    // never parsed is a type the next save deletes.
    const serviceTypeCodeableConcept = getExtensionValue(ext, 'serviceType') as CodeableConcept | undefined;
    const appointmentTypeCodings = (serviceTypeCodeableConcept?.coding ?? []).filter((c) => !!c.code);

    if (appointmentTypeCodings.length === 0) {
      continue;
    }

    const key = `${dayOfWeek}|${startTime}|${endTime}`;
    if (!timeRangeMap.has(key)) {
      timeRangeMap.set(key, {
        startTime,
        endTime,
        appointmentTypes: [],
      });
    }
    for (const appointmentTypeCoding of appointmentTypeCodings) {
      // Check if this coding is already in the array (avoid duplicates)
      const existingCoding = timeRangeMap
        .get(key)!
        .appointmentTypes.find(
          (c) => c.code === appointmentTypeCoding.code && c.system === appointmentTypeCoding.system
        );
      if (!existingCoding) {
        timeRangeMap.get(key)!.appointmentTypes.push(appointmentTypeCoding);
      }
    }
  }

  // Build days array
  const days = DAYS_OF_WEEK.map((dayOption) => {
    const dayOfWeek = dayOption.value;
    const timeRanges: TimeRange[] = [];

    // Find all time ranges for this day
    for (const [key, value] of timeRangeMap.entries()) {
      const [day, ,] = key.split('|');
      if (day === dayOfWeek) {
        timeRanges.push({
          startTime: value.startTime,
          endTime: value.endTime,
          appointmentTypes: value.appointmentTypes,
        });
      }
    }

    // Sort time ranges by start time
    timeRanges.sort((a, b) => {
      const aStart = convertTimeToHHMMSS(a.startTime);
      const bStart = convertTimeToHHMMSS(b.startTime);
      return aStart.localeCompare(bStart);
    });

    return {
      dayOfWeek,
      enabled: timeRanges.length > 0,
      timeRanges,
    };
  });

  return {
    practitioner: defaultPractitioner,
    encounterClass,
    timezone,
    location,
    days,
  };
}

const formatTimeOptionalMinutes = (d: dayjs.Dayjs): string => d.format(d.minute() !== 0 ? 'h:mm a' : 'h a');

const availabilityToDisplay = (daysOfWeek: string[], timeOfDay: string, duration: number, durationUnit: string) => {
  const daysOfWeekString = daysOfWeek.map((day: string) => capitalize(day)).join(', ');
  const durationStart = dayjs()
    .set('hour', Number(timeOfDay.split(':')[0]))
    .set('minute', Number(timeOfDay.split(':')[1]));
  const durationEnd = durationStart.add(duration, durationUnit as 'h');
  return `${daysOfWeekString} - ${formatTimeOptionalMinutes(durationStart)} to ${formatTimeOptionalMinutes(durationEnd)}`;
};

const mapConsolidatedAvailabilityToEntry = (
  consolidatedAvailabilityEntries: ConsolidatedAvailabilityEntry[],
  locations: Location[],
  practitionerRole: PractitionerRole,
  availabilityEntries: Extension[]
) => {
  // Since they are the same on weekly we can get any of them, same for modality
  const locationReference = availabilityEntries?.map((e) => e?.extension?.find((e) => e.url === 'location'))?.[0]
    ?.valueReference?.reference;
  const encounterClassCode = availabilityEntries?.map((e) => e?.extension?.find((e) => e.url === 'encounterClass'))?.[0]
    ?.valueCoding?.code;

  return {
    id: practitionerRole.id!,
    type: AvailabilityTypeEnum.WEEKLY,
    location: locations.find((l) => getReferenceString(l) === locationReference)?.name || 'N/A',
    locationReference: locationReference,
    modality: (encounterClassCode === 'VR' ? 'Virtual' : 'In Person') as AvailabilityModality,
    schedule: consolidatedAvailabilityEntries.map((e) =>
      availabilityToDisplay(Array.from(e.daysOfWeek.values()), e.startTime, e.duration, e.durationUnit)
    ),
    services: [],
  };
};

export const mapSlotToAvailabilityEntry = (slot: Slot, locations: Location[]) => {
  const locationReference = slot.extension?.map((ext) => ext.extension?.find((e) => e.url === 'location'))?.[0]
    ?.valueReference?.reference;
  const encounterClassCode = slot.extension?.map((ext) => ext.extension?.find((e) => e.url === 'encounterClass'))?.[0]
    ?.valueCoding?.code;

  return {
    id: slot.id!,
    type: AvailabilityTypeEnum.ONETIME,
    location: locations.find((l) => getReferenceString(l) === locationReference)?.name || 'N/A',
    locationReference: locationReference,
    modality: (encounterClassCode === 'VR' ? 'Virtual' : 'In Person') as AvailabilityModality,
    schedule: [
      slot.start && slot.end
        ? `${dayjs(slot.start).format('MMM D, YYYY')} - ${formatTimeOptionalMinutes(dayjs(slot.start))} to ${formatTimeOptionalMinutes(dayjs(slot.end))}`
        : '',
    ],
    services:
      slot.serviceType?.map((serviceType) => serviceType?.coding?.[0]?.display).filter((e) => e !== undefined) || [],
  };
};

/**
 * Parse a Slot resource into OneTimeAvailabilityFormData for editing.
 * Converts UTC start/end to the slot's timezone for date and time display.
 */
export function parseSlotToOneTimeFormData(
  slot: Slot,
  practitionerRef: Reference<Practitioner>,
  defaultTimezone: string
): OneTimeAvailabilityFormData {
  const schedulingExt = slot.extension?.find((ext) => ext.url === SCHEDULING_PARAMETERS_EXTENSION);
  if (!schedulingExt) {
    throw new Error('No scheduling extension found in slot: ' + getReferenceString(slot));
  }
  const timezoneStr = (getExtensionValue(schedulingExt, 'timezone') as string) || defaultTimezone;
  const encounterClass =
    (getExtensionValue(schedulingExt, 'encounterClass') as Coding) || VIRTUAL_ENCOUNTER_CLASS_CODING;
  const locationRef = getExtensionValue(schedulingExt, 'location') as Reference<Location> | undefined;
  const location = locationRef?.reference ? ({ reference: locationRef.reference } as Reference<Location>) : null;

  const startInTz = slot.start && timezoneStr ? dayjs.utc(slot.start).tz(timezoneStr) : dayjs().tz(defaultTimezone);
  const endInTz = slot.end && timezoneStr ? dayjs.utc(slot.end).tz(timezoneStr) : startInTz.add(1, 'hour');

  const appointmentTypes: Coding[] =
    slot.serviceType?.flatMap((st) => st?.coding?.filter((c): c is Coding => !!c) ?? []) ?? [];

  const schedulingGuideline = getExtensionValue(schedulingExt, SCHEDULING_GUIDELINE_PARAMETER) as string | undefined;

  return {
    practitioner: practitionerRef,
    encounterClass,
    timezone: timezoneStr,
    location,
    date: startInTz.format('YYYY-MM-DD'),
    startTime: startInTz.format('HH:mm'),
    endTime: endInTz.format('HH:mm'),
    appointmentTypes,
    schedulingGuideline,
  };
}

type AvailabilityResources = {
  schedules: Schedule[];
};

type AvailabilityFilters = {
  roles: string[];
  /** PractitionerRole reference strings or `Reference` objects (e.g. from hooks). */
  providers: (Reference<PractitionerRole> | string)[];
};

type AvailabilityQuery = {
  'service-category': string;
  _count: number;
  actor?: string;
  'actor:PractitionerRole.role'?: string;
};

export const loadOneTimeAvailabilityResources = async (medplum: MedplumClient, scheduleReference: string) => {
  const slots = await medplum.searchResources('Slot', {
    schedule: scheduleReference,
    'service-category': `${BARIENDO_CT_SYSTEM}|${ScheduleCategory.APPOINTMENT_AVAILABILITY}`,
    status: 'free',
    _sort: 'start',
    start: `ge${dayjs().add(-1, 'day').toISOString()}`,
  });
  return slots;
};

type ConsolidatedAvailabilityEntry = {
  startTime: string;
  duration: number;
  durationUnit: string;
  daysOfWeek: Set<string>;
};

export async function loadFilterResources(medplum: MedplumClient) {
  const locationPromise = medplum.searchResources('Location');
  const resourcePromise: Promise<ResourceArray<Resource>> = medplum.searchResources('PractitionerRole', {
    _count: '10000',
    '_include:iterate': 'PractitionerRole:practitioner',
    'active:not': false,
  });

  const [locations, resources] = await Promise.all([locationPromise, resourcePromise]);

  const practitioners = resources.filter((r) => isResource(r, 'Practitioner')) as Practitioner[];
  const practitionerRoles = resources.filter((r) => isResource(r, 'PractitionerRole')) as PractitionerRole[];

  practitioners.sort((a, b) => {
    const nameA = formatHumanName(a.name?.[0]) ?? '';
    const nameB = formatHumanName(b.name?.[0]) ?? '';
    return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
  });

  practitionerRoles.sort((a, b) => {
    const nameA = a.practitioner?.display ?? '';
    const nameB = b.practitioner?.display ?? '';
    return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
  });

  return {
    practitioners,
    practitionerRoles,
    locations,
  };
}

export async function loadAvailabilityResources(
  medplum: MedplumClient,
  filters: AvailabilityFilters
): Promise<AvailabilityResources> {
  const query: AvailabilityQuery = {
    'service-category': 'appointment-availability',
    _count: 1000,
  };

  if (filters.providers.length > 0) {
    query['actor'] = filters.providers
      .map((p) => (typeof p === 'string' ? p : (getReferenceString(p) ?? '')))
      .filter(Boolean)
      .join(',');
  }

  if (filters.roles.length > 0) {
    query['actor:PractitionerRole.role'] = filters.roles.join(',');
  }

  const schedules = await medplum.searchResources('Schedule', query);

  return {
    schedules,
  };
}

export function getWeeklyAvailabilityEntry(
  schedules: Schedule[],
  practitionerRoles: PractitionerRole[],
  practitioners: Practitioner[],
  locations: Location[]
): ProviderAvailability[] {
  const providersAvailability: ProviderAvailability[] = [];

  for (const practitionerRole of practitionerRoles) {
    const practitioner = practitioners.find((p) => getReferenceString(p) === practitionerRole.practitioner?.reference);
    if (!practitioner) continue;

    const practitionerSchedules = schedules.filter(
      (s) => s.actor && s.actor.some((a) => a.reference === getReferenceString(practitionerRole))
    );

    const availabilityEntries = practitionerSchedules
      .flatMap((s) => s.extension ?? [])
      .filter((e) => e.url === SCHEDULING_PARAMETERS_EXTENSION);

    const availabilities: AvailabilityEntry[] = [];

    const consolidatedAvailabilityMapValues = availabilityEntries
      .flatMap((e) => e.extension ?? [])
      .filter((e) => e.url === 'availability')
      .reduce((acumulator: Map<string, ConsolidatedAvailabilityEntry>, current: Extension) => {
        const dayOfWeek = current.valueTiming?.repeat?.dayOfWeek?.[0] ?? '';
        const timeOfDay = current.valueTiming?.repeat?.timeOfDay?.[0] ?? '';
        const duration = current.valueTiming?.repeat?.duration ?? 0;
        const durationUnit = current.valueTiming?.repeat?.durationUnit ?? '';

        const key = `${timeOfDay}-${duration}-${durationUnit}`;
        if (!acumulator.has(key)) {
          acumulator.set(key, {
            startTime: timeOfDay,
            duration,
            durationUnit,
            daysOfWeek: new Set([dayOfWeek]),
          });
        } else {
          acumulator.get(key)?.daysOfWeek.add(dayOfWeek);
        }

        return acumulator;
      }, new Map<string, ConsolidatedAvailabilityEntry>())
      .values();

    const consolidatedAvailabilityEntries = Array.from(consolidatedAvailabilityMapValues);

    // We have only one entry for weekly availability
    if (consolidatedAvailabilityEntries.length > 0) {
      availabilities.push(
        mapConsolidatedAvailabilityToEntry(
          consolidatedAvailabilityEntries,
          locations,
          practitionerRole,
          availabilityEntries
        )
      );
    }

    if (availabilities.length === 0) {
      continue;
    }

    providersAvailability.push({
      providerId: getReferenceString(practitioner)!,
      providerName: formatHumanName(practitioner?.name?.[0]),
      providerInitials: getAvatarInitials(practitioner?.name?.[0]),
      providerScheduleReference: getReferenceString(practitionerSchedules?.[0]),
      availabilities: availabilities as AvailabilityEntry[],
    });
  }

  return providersAvailability;
}
